import { ld } from "@/lib/theme/light-dark";
import {
  BookCheck,
  BookOpen,
  ChartColumn,
  Check,
  ChevronDown,
  FileInput,
  Landmark,
  Receipt,
} from "lucide-react";
/**
 * HERO app-window module: Buchhaltung (beta). Blurred invoice + booking mock
 * behind a frosted beta card linking to the sign-up form.
 */
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { DarkButton } from "../../hero-primitives";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

const MONO_GROUP: CSSProperties = {
  fontFamily: H.label,
  fontSize: 10,
  letterSpacing: "0.12em",
  color: H.inkGhost,
  textTransform: "uppercase",
};

const SIDE_ROW: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "7px 9px",
  borderRadius: 8,
  fontSize: 13.5,
  color: H.inkSoft,
};

const FIELD_LABEL: CSSProperties = {
  fontSize: 9.5,
  fontFamily: H.label,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
};

const FIELD_BOX: CSSProperties = {
  height: 33,
  background: ld("#fff", "#181b1f"),
  border: `1px solid ${ld("#E2E5EA", "#36393d")}`,
  borderRadius: 7,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "0 11px",
  fontSize: 12.5,
  color: H.ink,
};

const CHEVRON = (
  <span
    style={{
      marginLeft: "auto",
      color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
      display: "inline-flex",
    }}
  >
    <ChevronDown width={14} height={14} strokeWidth={1.8} />
  </span>
);

/** Blurred sidebar (decorative — non-interactive). */
function BuchhaltungSidebar(): ReactNode {
  const t = useTranslations("marketing.moduleBuchhaltung");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#FBFBFC", "#1d1f23"),
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          filter: "blur(3px)",
          opacity: 0.5,
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 6px 12px",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700 }}>
            {t("buchhaltung")}
          </span>
        </div>
        <div style={{ ...MONO_GROUP, padding: "4px 9px 8px" }}>
          {t("belege")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={SIDE_ROW}>
            <span style={{ color: ld("#4C8C6B", "#7bbc98"), display: "inline-flex" }}>
              <Receipt width={16} height={16} strokeWidth={1.8} />
            </span>
            <span style={{ flex: 1 }}>{t("offenePosten")}</span>
            <span style={{ fontSize: 12, color: H.inkGhost }}>12</span>
          </div>
          <div style={SIDE_ROW}>
            <span style={{ color: ld("#4F77AE", "#83ade8"), display: "inline-flex" }}>
              <FileInput width={16} height={16} strokeWidth={1.8} />
            </span>
            <span style={{ flex: 1 }}>{t("rechnungseingang")}</span>
          </div>
          <div style={SIDE_ROW}>
            <span style={{ color: ld("#A8824B", "#cba36c"), display: "inline-flex" }}>
              <Landmark width={16} height={16} strokeWidth={1.8} />
            </span>
            <span style={{ flex: 1 }}>{t("bankKasse")}</span>
          </div>
        </div>
        <div style={{ ...MONO_GROUP, padding: "18px 9px 8px" }}>
          {t("konten")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={SIDE_ROW}>
            <span style={{ color: ld("#6B7280", "#a6aaaf"), display: "inline-flex" }}>
              <BookOpen width={16} height={16} strokeWidth={1.8} />
            </span>
            <span style={{ flex: 1 }}>{t("kontenrahmenSkr04")}</span>
          </div>
          <div style={SIDE_ROW}>
            <span style={{ color: ld("#7A6CB0", "#ac9fe7"), display: "inline-flex" }}>
              <ChartColumn width={16} height={16} strokeWidth={1.8} />
            </span>
            <span style={{ flex: 1 }}>{t("auswertungen")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Blurred invoice skeleton (left card of the frosted content). */
function InvoiceSkeleton(): ReactNode {
  const t = useTranslations("marketing.moduleBuchhaltung");
  const positions: ReadonlyArray<{
    readonly bar: number;
    readonly mng: string;
    readonly einzel: string;
    readonly betrag: string;
  }> = [
    { bar: 150, mng: "1", einzel: "480,00", betrag: "480,00" },
    { bar: 184, mng: "1", einzel: "620,00", betrag: "620,00" },
    { bar: 120, mng: "1", einzel: "340,00", betrag: "340,00" },
    { bar: 168, mng: "4", einzel: "350,00", betrag: "1.400,00" },
  ];
  return (
    <div
      style={{
        flex: "1.08",
        minWidth: 0,
        background: ld("#fff", "#181b1f"),
        border: `1px solid ${ld("#E6E8EC", "#33363b")}`,
        borderRadius: 7,
        boxShadow: `0 22px 48px -26px ${ld("rgba(11,15,25,0.24)", "rgba(0,0,0,0.41)")}`,
        padding: "24px 26px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: H.ink,
              flexShrink: 0,
            }}
          />
          <div>
            <div
              style={{
                height: 9,
                width: 104,
                background: ld("#D2D7DD", "#42464a"),
                borderRadius: 3,
              }}
            />
            <div
              style={{
                height: 6,
                width: 70,
                background: ld("#E7EAEE", "#323539"),
                borderRadius: 3,
                marginTop: 6,
              }}
            />
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: H.ink,
            }}
          >
            {t("rechnung")}
          </div>
          <div style={{ fontSize: 11, color: H.inkFaint, marginTop: 5 }}>
            {t("nr2026014712")}
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: 18,
          display: "flex",
          flexDirection: "column",
          gap: 5,
        }}
      >
        <div
          style={{
            height: 7,
            width: 150,
            background: ld("#E2E5EA", "#36393d"),
            borderRadius: 3,
          }}
        />
        <div
          style={{
            height: 6,
            width: 190,
            background: ld("#ECEEF1", "#2e3135"),
            borderRadius: 3,
          }}
        />
        <div
          style={{
            height: 6,
            width: 130,
            background: ld("#ECEEF1", "#2e3135"),
            borderRadius: 3,
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 18,
          paddingBottom: 7,
          borderBottom: `1.5px solid ${ld("#E6E8EC", "#33363b")}`,
          fontSize: 9.5,
          fontFamily: H.label,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
        }}
      >
        <span style={{ flex: 1 }}>{t("position")}</span>
        <span style={{ width: 22, textAlign: "right" }}>{t("mng")}</span>
        <span style={{ width: 66, textAlign: "right" }}>{t("einzel")}</span>
        <span style={{ width: 74, textAlign: "right" }}>{t("betrag")}</span>
      </div>
      {positions.map((p) => (
        <div
          key={p.bar}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "7px 0",
            borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
            fontSize: 11,
            color: H.inkMute,
          }}
        >
          <div
            style={{
              flex: 1,
              height: 7,
              background: ld("#E2E5EA", "#36393d"),
              borderRadius: 3,
              maxWidth: p.bar,
            }}
          />
          <span
            style={{
              width: 22,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {p.mng}
          </span>
          <span
            style={{
              width: 66,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {p.einzel}
          </span>
          <span
            style={{
              width: 74,
              textAlign: "right",
              color: H.ink,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {p.betrag}
          </span>
        </div>
      ))}
      <div
        style={{
          marginTop: 18,
          display: "flex",
          flexDirection: "column",
          gap: 7,
          alignItems: "flex-end",
        }}
      >
        <div
          style={{ display: "flex", gap: 40, fontSize: 12, color: H.inkMute }}
        >
          <span>{t("zwischensumme")}</span>
          <span
            style={{
              width: 84,
              textAlign: "right",
              color: H.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {t("k284000")}
          </span>
        </div>
        <div
          style={{ display: "flex", gap: 40, fontSize: 12, color: H.inkMute }}
        >
          <span>{t("zzglUst19")}</span>
          <span
            style={{
              width: 84,
              textAlign: "right",
              color: H.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {t("k53960")}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            gap: 40,
            fontSize: 14,
            fontWeight: 700,
            color: H.ink,
            borderTop: `1.5px solid ${ld("#E6E8EC", "#33363b")}`,
            paddingTop: 8,
          }}
        >
          <span>{t("gesamtbetrag")}</span>
          <span
            style={{
              width: 84,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {t("k337960")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Blurred booking form skeleton (right card of the frosted content). */
function BookingFormSkeleton(): ReactNode {
  const t = useTranslations("marketing.moduleBuchhaltung");
  return (
    <div
      style={{
        width: 296,
        flexShrink: 0,
        background: ld("#FAFBFC", "#1d2024"),
        border: `1px solid ${ld("#E6E8EC", "#33363b")}`,
        borderRadius: 7,
        padding: "20px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 14,
          fontWeight: 700,
          color: H.ink,
        }}
      >
        <span style={{ color: H.accent, display: "inline-flex" }}>
          <BookCheck width={17} height={17} strokeWidth={1.9} />
        </span>
        {t("belegVerbuchen")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={FIELD_LABEL}>{t("rechnungsdatum")}</span>
        <div style={FIELD_BOX}>12.06.2026</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={FIELD_LABEL}>{t("rechnungsadressat")}</span>
        <div style={FIELD_BOX}>
          {t("wegAkazienhof")}
          {CHEVRON}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={FIELD_LABEL}>{t("belegnummer")}</span>
        <div style={FIELD_BOX}>{t("k20260147")}</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={FIELD_LABEL}>{t("sachkonto")}</span>
        <div style={FIELD_BOX}>
          {t("k4250Instandhaltung")}
          {CHEVRON}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={FIELD_LABEL}>{t("kostenstelle")}</span>
        <div style={FIELD_BOX}>
          {t("liegenschaft014")}
          {CHEVRON}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={FIELD_LABEL}>{t("umsatzsteuer")}</span>
        <div
          style={{
            display: "inline-flex",
            padding: 3,
            background: ld("#EEF0F3", "#2c2f33"),
            borderRadius: 8,
            gap: 2,
            alignSelf: "flex-start",
          }}
        >
          <span
            style={{
              padding: "5px 12px",
              borderRadius: 6,
              background: ld("#fff", "#181b1f"),
              boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.08)", "rgba(0,0,0,0.17)")}`,
              fontSize: 12,
              fontWeight: 600,
              color: H.ink,
            }}
          >
            {t("k19")}
          </span>
          <span
            style={{ padding: "5px 12px", fontSize: 12, color: H.inkFaint }}
          >
            {t("k7")}
          </span>
          <span
            style={{ padding: "5px 12px", fontSize: 12, color: H.inkFaint }}
          >
            {t("k0")}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <div
          style={{
            flex: 1,
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#E2E5EA", "#36393d")}`,
            borderRadius: 7,
            padding: "7px 10px",
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {t("netto")}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: H.ink }}>
            {t("k284000")}
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#E2E5EA", "#36393d")}`,
            borderRadius: 7,
            padding: "7px 10px",
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {t("brutto")}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: H.ink }}>
            {t("k337960")}
          </div>
        </div>
      </div>
      <button
        type="button"
        style={{
          appearance: "none",
          cursor: "pointer",
          fontFamily: H.sans,
          marginTop: 2,
          height: 38,
          border: "none",
          borderRadius: 8,
          background: H.accent,
          color: ld("#fff", "#181b1f"),
          fontSize: 13,
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
        }}
      >
        <Check width={16} height={16} strokeWidth={2.4} />
        {t("buchen")}
      </button>
    </div>
  );
}

/** Content: blurred mock + frosted beta card. */
function BuchhaltungContent(): ReactNode {
  const t = useTranslations("marketing.moduleBuchhaltung");
  return (
    <div
      style={{
        flex: 1,
        padding: "20px 22px",
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      <div
        style={{
          position: "relative",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            filter: "blur(5px)",
            display: "flex",
            gap: 18,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <InvoiceSkeleton />
          <BookingFormSkeleton />
        </div>

        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              `linear-gradient(180deg, ${ld("rgba(252,252,251,0.42)", "rgba(28,31,34,0.42)")} 0%, ${ld("rgba(252,252,251,0.66)", "rgba(28,31,34,0.66)")} 100%)`,
          }}
        />

        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              maxWidth: 432,
              textAlign: "center",
              background: ld("rgba(255,255,255,0.92)", "rgba(26,28,31,0.92)"),
              backdropFilter: "blur(10px) saturate(1.4)",
              WebkitBackdropFilter: "blur(10px) saturate(1.4)",
              border: `1px solid ${ld("rgba(11,15,25,0.07)", "rgba(250,250,250,0.07)")}`,
              borderRadius: 20,
              boxShadow:
                `0 44px 100px -36px ${ld("rgba(11,15,25,0.44)", "rgba(0,0,0,0.71)")}, 0 14px 34px -18px ${ld("rgba(11,15,25,0.22)", "rgba(0,0,0,0.38)")}`,
              padding: "28px 34px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: H.accentSoft,
                color: H.accent,
                fontFamily: H.label,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                padding: "5px 11px",
                borderRadius: 999,
              }}
            >
              <span
                className="mkhero-kp-live"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: H.accent,
                }}
              />
              {t("betaBadge")}
            </span>
            <h3
              style={{
                fontFamily: H.display,
                fontWeight: 500,
                fontSize: 25,
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
                color: H.ink,
                margin: "14px 0 0",
              }}
            >
              {t("betaTitel")}
            </h3>
            <p
              style={{
                fontSize: 13.5,
                lineHeight: 1.62,
                color: ld("rgba(11,15,25,0.62)", "rgba(250,250,250,0.62)"),
                margin: "10px 0 0",
              }}
            >
              {t("betaHinweis")}
            </p>
            <DarkButton
              href="/demo"
              tone="cta"
              style={{ marginTop: 20, padding: "11px 20px", fontSize: 14, borderRadius: 10 }}
            >
              {t("betaFormular")}
            </DarkButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Buchhaltung beta module (sidebar + frosted content). */
export function ModuleBuchhaltung(_props: ModuleProps): ReactNode {
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT, background: ld("#fff", "#181b1f") }}>
      <BuchhaltungSidebar />
      <BuchhaltungContent />
    </div>
  );
}
